// A best-first search for a way to the Monad. It proves every rite can be
// won, rates how hard a deal is and watches for dead ends.
// It shares the engine, so it can never break a law.

import { reversed } from "./arrays.ts"
import { applyMove, breaks, cardCount, heldCards, isWon, legalMoves, stateKey, type Move, type TableState } from "./engine.ts"

export type Solution =
  | { readonly tag: "solved"; readonly moves: readonly Move[]; readonly nodes: number }
  | { readonly tag: "dead"; readonly nodes: number } // every reachable table was tried
  | { readonly tag: "unknown"; readonly nodes: number } // the search gave up first

export type SolveOptions = { readonly maxNodes: number; readonly weight?: number }

type Path = { readonly tag: "root" } | { readonly tag: "step"; readonly parent: SearchNode; readonly move: Move }
type SearchNode = { readonly state: TableState; readonly g: number; readonly f: number; readonly path: Path }

// A guess at the moves still needed. Every card sitting on a card it does
// not rest on must move; so must every card on an altar. Fewer cards left
// means more folds done, which breaks ties toward progress.
export const estimate = (state: TableState): number =>
  breaks(state) + heldCards(state.altars).length + 0.25 * (cardCount(state) - 1)

// ---------- a binary heap over the search's own scratch list ----------

const heapPush = (a: SearchNode[], x: SearchNode): void => {
  a.push(x)
  let i = a.length - 1
  while (i > 0) {
    const p = (i - 1) >> 1
    const parent = a[p]
    if (parent === undefined || parent.f <= x.f) break
    a[i] = parent
    i = p
  }
  a[i] = x
}

const heapPop = (a: SearchNode[]): SearchNode | undefined => {
  const top = a[0]
  const last = a.pop()
  if (last === undefined || a.length === 0) return top
  let i = 0
  for (;;) {
    const l = 2 * i + 1
    const r = l + 1
    const left = a[l]
    const right = a[r]
    let m = i
    let mf = last.f
    if (left !== undefined && left.f < mf) {
      m = l
      mf = left.f
    }
    if (right !== undefined && right.f < mf) m = r
    const child = a[m]
    if (m === i || child === undefined) break
    a[i] = child
    i = m
  }
  a[i] = last
  return top
}

const movesTo = (node: SearchNode, last: Move): readonly Move[] => {
  const moves: Move[] = [last]
  for (let p = node.path; p.tag === "step"; p = p.parent.path) moves.push(p.move)
  return reversed(moves)
}

// Weighted best-first search (weight 2 by default): fast, not always the
// shortest path, which is fine for par.
export const solve = (start: TableState, { maxNodes, weight = 2 }: SolveOptions): Solution => {
  if (isWon(start)) return { tag: "solved", moves: [], nodes: 0 }
  const heap: SearchNode[] = []
  const best = new Map<string, number>([[stateKey(start), 0]])
  heapPush(heap, { state: start, g: 0, f: 0, path: { tag: "root" } })
  let nodes = 0
  for (let node = heapPop(heap); node !== undefined; node = heapPop(heap)) {
    if (++nodes > maxNodes) return { tag: "unknown", nodes }
    for (const m of legalMoves(node.state, { prune: true })) {
      const out = applyMove(node.state, m)
      if (out.tag === "error") continue
      const state = out.value.state
      const g = node.g + 1
      if (isWon(state)) return { tag: "solved", moves: movesTo(node, m), nodes }
      const key = stateKey(state)
      const prev = best.get(key)
      if (prev !== undefined && prev <= g) continue
      best.set(key, g)
      heapPush(heap, { state, g, f: g + weight * estimate(state), path: { tag: "step", parent: node, move: m } })
    }
  }
  return { tag: "dead", nodes }
}
